import { useEffect } from "react";
import { Button } from "./Button";

// Placeholder UI that mirrors the eventual booking shape. When the
// provider-network API lands, swap the disabled controls for real
// data-driven ones (provider list, available dates, real time slots).
const PLACEHOLDER_SLOTS = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM",
  "11:00 AM", "11:30 AM", "1:00 PM", "1:30 PM",
  "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM",
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export function BookingDialog({ open, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="booking-title"
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4 py-6 overflow-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-lg bg-white rounded-card border border-fh-border shadow-card p-6">
        <h2 id="booking-title" className="font-serif text-[22px] text-fh-ink mb-1">
          Book an appointment
        </h2>
        <p className="text-[14px] text-fh-gray mb-4">
          Schedule a video visit with a licensed clinician.
        </p>

        <div className="bg-fh-sage border border-fh-green-light rounded-[10px] px-3.5 py-3 mb-5 text-[13px] text-fh-green-dark leading-snug">
          Live scheduling is being connected to our provider network. Until that's live, email{" "}
          <a href="mailto:care@fortehealth.co" className="font-semibold underline">
            care@fortehealth.co
          </a>{" "}
          and we'll book you a visit.
        </div>

        <div className="mb-4">
          <label className="block text-[13px] font-semibold text-fh-green-dark mb-1.5">
            Provider
          </label>
          <div className="rounded-[10px] border border-fh-border bg-fh-cream px-3.5 py-3 text-[14px] text-fh-gray-light flex items-center justify-between">
            <span>Loading providers from network…</span>
            <span className="inline-block w-3 h-3 rounded-full border-2 border-fh-gray-light border-r-transparent animate-spin" />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-[13px] font-semibold text-fh-green-dark mb-1.5">
            Available date
          </label>
          <div className="rounded-[10px] border border-fh-border bg-fh-cream px-3.5 py-3 text-[14px] text-fh-gray-light">
            Loading availability…
          </div>
        </div>

        <div className="mb-5">
          <label className="block text-[13px] font-semibold text-fh-green-dark mb-1.5">
            Time slots
          </label>
          <div className="grid grid-cols-4 gap-2">
            {PLACEHOLDER_SLOTS.map((t) => (
              <button
                key={t}
                type="button"
                disabled
                className="rounded-md border border-fh-border bg-fh-cream text-fh-gray-light text-[12px] font-semibold py-2 cursor-not-allowed"
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
          <a
            href="mailto:care@fortehealth.co?subject=Appointment%20request"
            className="inline-flex items-center justify-center gap-2 rounded-[8px] bg-fh-green text-white text-sm font-semibold px-3 py-2 hover:bg-fh-green-dark"
          >
            Email care team
          </a>
        </div>
      </div>
    </div>
  );
}
